/**
 * BetaFeedbackModal — the single in-app modal opened by EVERY feedback
 * CTA (dashboard card, floating button, banner, smart prompt, support
 * page). Replaces the older FeedbackModal.jsx which existed before the
 * Beta Feedback Architecture was centralised.
 *
 * Captures: rating (1-5 stars) + category + free text.
 * Posts to: POST /api/feedback/submit (existing endpoint).
 * Tracks via: BetaFeedbackContext.recordSubmitSuccess on success.
 *
 * Mobile-first: full-screen on small viewports, centered card on md+.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Star, Send, Heart } from 'lucide-react';
import { useBetaFeedback } from '@/contexts/BetaFeedbackContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: '💬 General feedback' },
  { value: 'bug', label: '🐞 Bug / Something broke' },
  { value: 'feature_request', label: '✨ Feature idea' },
  { value: 'content', label: '📚 Health content feedback' },
  { value: 'praise', label: '💚 Praise / Compliment' },
];

const StarRow = ({ rating, hovered, setRating, setHovered }) => (
  <div className="flex items-center justify-center gap-1.5" data-testid="feedback-modal-stars">
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= (hovered || rating);
      return (
        <button
          key={n}
          type="button"
          aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
          className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 rounded-md"
          onClick={() => setRating(n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          data-testid={`feedback-star-${n}`}
        >
          <Star
            className={`h-7 w-7 sm:h-8 sm:w-8 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
          />
        </button>
      );
    })}
  </div>
);

const BetaFeedbackModal = () => {
  const { modalState, closeFeedbackModal, recordSubmitSuccess } = useBetaFeedback();
  const { open, source, prefill } = modalState;

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [category, setCategory] = useState('general');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Seed rating from prefill (Smart Feedback Prompt passes the star
  // rating the user already tapped) on every fresh open. Setting state
  // when `open` toggles is correct here — we're syncing derived form
  // state with the dialog lifecycle.
  useEffect(() => {
    if (!open) return;
    setRating(prefill?.rating || 0);
    setCategory(prefill?.category || 'general');
    setText(prefill?.text || '');
    setHovered(0);
  }, [open, prefill]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please pick a star rating to continue.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/feedback/submit`,
        {
          type: 'beta',
          rating,
          category,
          feedback_text: text.trim() || null,
          page: source,
        },
        { withCredentials: true },
      );
      recordSubmitSuccess(source, rating);
      toast.success('Thanks — your feedback shapes HealthLoop.', { icon: <Heart className="h-4 w-4 text-rose-500" /> });
      closeFeedbackModal();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) closeFeedbackModal(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="beta-feedback-modal">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <Heart className="h-4 w-4 text-emerald-600" />
            Help shape HealthLoop
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            You&apos;re in our beta. Your feedback prioritises what we build next — bugs, content, ideas, anything.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <p className="text-sm font-medium text-gray-800 mb-2 text-center">How is HealthLoop working for you?</p>
            <StarRow rating={rating} hovered={hovered} setRating={setRating} setHovered={setHovered} />
            <p className="text-[11px] text-gray-400 mt-1 text-center" data-testid="feedback-rating-label">
              {rating === 0 ? 'Tap a star' :
               rating <= 2 ? 'Sorry to hear that — tell us what to fix' :
               rating === 3 ? 'Thanks — what would make it better?' :
               rating === 4 ? 'Glad to hear it!' :
                              'You made our day 💚'}
            </p>
          </div>

          <div>
            <label htmlFor="bfm-category" className="text-xs text-gray-500 font-medium">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="bfm-category" data-testid="feedback-category-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label htmlFor="bfm-text" className="text-xs text-gray-500 font-medium">
              Anything to add? <span className="text-gray-400">(optional)</span>
            </label>
            <Textarea
              id="bfm-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell us what's working, what isn't, or what you'd love to see…"
              rows={4}
              maxLength={1200}
              className="mt-1"
              data-testid="feedback-text-input"
            />
            <p className="text-[10px] text-gray-400 text-right mt-0.5">{text.length}/1200</p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={closeFeedbackModal} disabled={submitting} data-testid="feedback-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || rating === 0} data-testid="feedback-submit-btn">
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {submitting ? 'Sending…' : 'Send feedback'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BetaFeedbackModal;
